import React, { useState } from 'react';
import { generateCampaignContent } from '../services/geminiService';
import { ContentType, Language, Tone } from '../types';
import { PenTool, Copy, FileText, Mic, MessageSquare, Check, Sparkles, Calendar, Clock, Facebook, Twitter, Instagram, Send, Trash2, Edit3, List } from 'lucide-react';

interface ScheduledPost {
  id: string;
  content: string;
  type: ContentType;
  date: string;
  time: string;
  platforms: string[];
}

const ContentStudio: React.FC = () => {
  const [topic, setTopic] = useState('');
  const [type, setType] = useState<ContentType>('Speech');
  const [language, setLanguage] = useState<Language>('English');
  const [tone, setTone] = useState<Tone>('Inspirational');
  const [generatedContent, setGeneratedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Scheduling State
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [isScheduled, setIsScheduled] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);

  // Scheduled Queue State
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPost[]>([
    {
      id: 'mock-1',
      content: 'Jai Hind! The progress in Borkhetri under the double engine sarkar is visible to all. From roads to ration, we are delivering. #Borkhetri2026',
      type: 'WhatsApp Message',
      date: '2025-10-25',
      time: '10:00',
      platforms: ['Twitter', 'Facebook']
    }
  ]);

  const handleGenerate = async () => {
    if (!topic) return;
    setLoading(true);
    setCopied(false);
    setShowSchedule(false);
    setIsScheduled(false);
    const result = await generateCampaignContent(type, topic, language, tone);
    setGeneratedContent(result);
    setLoading(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const togglePlatform = (platform: string) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };

  const handleScheduleConfirm = () => {
    if (!scheduleDate || !scheduleTime || selectedPlatforms.length === 0) {
      alert("Please select date, time, and at least one platform.");
      return;
    }
    
    setIsScheduling(true);
    
    // Simulate Network API call
    setTimeout(() => {
        const newPost: ScheduledPost = {
          id: Date.now().toString(),
          content: generatedContent,
          type: type,
          date: scheduleDate,
          time: scheduleTime,
          platforms: selectedPlatforms
        };

        setScheduledPosts([newPost, ...scheduledPosts]);
        setIsScheduling(false);
        setIsScheduled(true);
        
        // Auto-close success message after 2.5 seconds
        setTimeout(() => {
            setIsScheduled(false);
            setShowSchedule(false);
            setSelectedPlatforms([]);
            setScheduleDate('');
            setScheduleTime('');
            // Optional: Clear content to start fresh, or keep it. Keeping it for now.
        }, 2500);
    }, 1500);
  };

  const handleCancelPost = (id: string) => {
    if (confirm('Are you sure you want to cancel this scheduled post?')) {
      setScheduledPosts(scheduledPosts.filter(p => p.id !== id));
    }
  };

  const handleEditPost = (post: ScheduledPost) => {
    // Load data back into the editor
    setGeneratedContent(post.content);
    setType(post.type);
    setScheduleDate(post.date);
    setScheduleTime(post.time);
    setSelectedPlatforms(post.platforms);
    setShowSchedule(true); // Open the schedule panel
    
    // Remove from list (conceptually "moving" it to edit mode)
    setScheduledPosts(scheduledPosts.filter(p => p.id !== post.id));
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderFormattedText = (text: string) => {
    return text.split('\n').map((line, i) => (
      <p key={i} className={`mb-2 ${language === 'Assamese' ? 'font-serif text-lg' : 'font-sans'}`}>
        {line}
      </p>
    ));
  };

  return (
    <div className="p-6 bg-slate-50 min-h-screen space-y-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <PenTool className="text-bjp-saffron" />
          Srijan Studio (Content Gen)
        </h2>
        <p className="text-sm text-slate-500">Generate speeches, slogans, and messages instantly using AI.</p>
      </div>

      {/* Editor & Preview Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Input Controls */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 h-fit">
          <div className="space-y-6">
            
            {/* Content Type */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-3">Content Type</label>
              <div className="grid grid-cols-2 gap-3">
                {(['Speech', 'WhatsApp Message', 'Press Release', 'Slogan'] as ContentType[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => setType(t)}
                    className={`flex items-center gap-2 p-3 rounded-lg border text-sm font-medium transition-all ${
                      type === t 
                        ? 'bg-orange-50 border-bjp-saffron text-bjp-saffron shadow-sm' 
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {t === 'Speech' && <Mic className="w-4 h-4" />}
                    {t === 'WhatsApp Message' && <MessageSquare className="w-4 h-4" />}
                    {t === 'Press Release' && <FileText className="w-4 h-4" />}
                    {t === 'Slogan' && <Sparkles className="w-4 h-4" />}
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Language & Tone */}
            <div className="grid grid-cols-2 gap-4">
               <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Language</label>
                  <select 
                    value={language} 
                    onChange={(e) => setLanguage(e.target.value as Language)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bjp-saffron/20"
                  >
                    <option value="English">English</option>
                    <option value="Assamese">Assamese (অসমীয়া)</option>
                  </select>
               </div>
               <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Tone</label>
                  <select 
                    value={tone} 
                    onChange={(e) => setTone(e.target.value as Tone)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bjp-saffron/20"
                  >
                    <option value="Inspirational">Inspirational</option>
                    <option value="Aggressive">Aggressive (Political)</option>
                    <option value="Factual/Administrative">Factual</option>
                    <option value="Emotional">Emotional</option>
                  </select>
               </div>
            </div>

            {/* Topic Input */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Topic / Key Points</label>
              <textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="E.g. Inauguration of new embankment in Loharkatha, targeting opposition for inaction..."
                className="w-full h-32 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bjp-saffron/20 resize-none"
              ></textarea>
            </div>

            <button
              onClick={handleGenerate}
              disabled={loading || !topic}
              className="w-full bg-slate-900 text-white py-3 rounded-xl font-medium shadow-lg shadow-slate-900/10 hover:shadow-slate-900/20 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:translate-y-0 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Creating Content...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 text-bjp-saffron" />
                  Generate {type}
                </>
              )}
            </button>

          </div>
        </div>

        {/* Output & Scheduling Display */}
        <div className="flex flex-col gap-6">
          
          {/* Output Card */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-[500px]">
            <div className="p-4 border-b border-slate-100 bg-slate-50 rounded-t-xl flex justify-between items-center">
              <h3 className="font-bold text-slate-700 text-sm uppercase">Generated Draft</h3>
              {generatedContent && (
                <button 
                  onClick={handleCopy}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${copied ? 'bg-green-100 text-green-700' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                >
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied' : 'Copy Text'}
                </button>
              )}
            </div>
            
            <div className="flex-1 p-6 overflow-y-auto">
              {generatedContent ? (
                <div className="prose prose-sm prose-slate max-w-none">
                  {renderFormattedText(generatedContent)}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 opacity-50">
                  <FileText className="w-16 h-16 mb-4 text-slate-300" />
                  <p className="text-center text-sm">Your AI generated content<br/>will appear here.</p>
                </div>
              )}
            </div>
          </div>

          {/* Publishing Hub */}
          {generatedContent && (
             <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden animate-fade-in-up">
                <div className="p-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                   <h3 className="font-bold text-slate-700 text-sm uppercase flex items-center gap-2">
                     <Send className="w-4 h-4 text-bjp-saffron" /> Publishing Hub
                   </h3>
                   {!showSchedule && (
                     <button 
                       onClick={() => setShowSchedule(true)}
                       className="text-xs bg-slate-800 text-white px-3 py-1.5 rounded hover:bg-slate-700 transition-colors"
                     >
                       Schedule Post
                     </button>
                   )}
                </div>

                {!showSchedule ? (
                   <div className="p-4 flex gap-3">
                      <button className="flex-1 bg-green-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-600 flex items-center justify-center gap-2 shadow-sm">
                        <MessageSquare className="w-4 h-4" /> Share to WhatsApp
                      </button>
                      <button className="flex-1 bg-white border border-slate-300 text-slate-700 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 shadow-sm">
                        Save to Library
                      </button>
                   </div>
                ) : (
                   <div className="p-5 space-y-4">
                      {isScheduled ? (
                        <div className="bg-green-50 border border-green-100 rounded-lg p-4 flex flex-col items-center justify-center text-center animate-fade-in">
                           <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-2">
                              <Check className="w-6 h-6 text-green-600" />
                           </div>
                           <h4 className="font-bold text-green-800">Post Scheduled Successfully!</h4>
                           <p className="text-xs text-green-600 mt-1">
                              Will be published on {scheduleDate} at {scheduleTime}
                           </p>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                               <label className="block text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" /> Date
                               </label>
                               <input 
                                 type="date" 
                                 value={scheduleDate}
                                 onChange={(e) => setScheduleDate(e.target.value)}
                                 className="w-full p-2 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-bjp-saffron/20 outline-none" 
                               />
                            </div>
                            <div>
                               <label className="block text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> Time
                               </label>
                               <input 
                                 type="time" 
                                 value={scheduleTime}
                                 onChange={(e) => setScheduleTime(e.target.value)}
                                 className="w-full p-2 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-bjp-saffron/20 outline-none" 
                               />
                            </div>
                          </div>
                          
                          <div>
                             <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Target Platforms</label>
                             <div className="flex gap-3">
                                {[
                                  { id: 'Facebook', icon: <Facebook className="w-4 h-4" /> },
                                  { id: 'Twitter', icon: <Twitter className="w-4 h-4" /> },
                                  { id: 'Instagram', icon: <Instagram className="w-4 h-4" /> }
                                ].map((p) => (
                                   <button 
                                      key={p.id}
                                      onClick={() => togglePlatform(p.id)}
                                      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded border text-sm transition-all ${selectedPlatforms.includes(p.id) ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                                   >
                                      {p.icon} {p.id}
                                   </button>
                                ))}
                             </div>
                          </div>

                          <div className="flex gap-3 pt-2">
                             <button 
                               onClick={() => setShowSchedule(false)}
                               className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded"
                             >
                               Cancel
                             </button>
                             <button 
                               onClick={handleScheduleConfirm}
                               disabled={isScheduling}
                               className="flex-1 bg-bjp-saffron text-white py-2 rounded text-sm font-medium hover:bg-orange-600 shadow-sm flex items-center justify-center gap-2 disabled:opacity-70"
                             >
                               {isScheduling ? (
                                 <>
                                   <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                   Scheduling...
                                 </>
                               ) : (
                                 'Confirm Schedule'
                               )}
                             </button>
                          </div>
                        </>
                      )}
                   </div>
                )}
             </div>
          )}

        </div>
      </div>

      {/* Scheduled Campaign Queue */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
         <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
           <h3 className="font-bold text-slate-800 flex items-center gap-2">
             <List className="w-5 h-5 text-bjp-saffron" /> Scheduled Campaign Queue
           </h3>
           <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded-full">{scheduledPosts.length} pending</span>
         </div>
         
         <div className="p-0">
           {scheduledPosts.length === 0 ? (
             <div className="p-8 text-center text-slate-400">
               <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
               <p className="text-sm">No campaigns scheduled. Use the Publishing Hub to schedule posts.</p>
             </div>
           ) : (
             <div className="divide-y divide-slate-100">
               {scheduledPosts.map((post) => (
                 <div key={post.id} className="p-4 hover:bg-slate-50 transition-colors flex flex-col md:flex-row gap-4 items-start md:items-center">
                   
                   {/* Info */}
                   <div className="flex-1 min-w-0">
                     <div className="flex items-center gap-2 mb-1">
                       <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold border ${post.type === 'Speech' ? 'bg-purple-50 text-purple-600 border-purple-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                         {post.type}
                       </span>
                       <div className="flex items-center gap-1 text-xs text-slate-500">
                         <Calendar className="w-3 h-3" /> {post.date}
                         <span className="mx-1">•</span>
                         <Clock className="w-3 h-3" /> {post.time}
                       </div>
                     </div>
                     <p className="text-sm text-slate-700 truncate font-medium">{post.content}</p>
                   </div>

                   {/* Platforms */}
                   <div className="flex gap-1">
                     {post.platforms.map(p => (
                       <div key={p} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 border border-slate-200" title={p}>
                         {p === 'Facebook' && <Facebook className="w-4 h-4" />}
                         {p === 'Twitter' && <Twitter className="w-4 h-4" />}
                         {p === 'Instagram' && <Instagram className="w-4 h-4" />}
                       </div>
                     ))}
                   </div>

                   {/* Actions */}
                   <div className="flex gap-2 w-full md:w-auto justify-end">
                     <button 
                       onClick={() => handleEditPost(post)}
                       className="p-2 text-slate-400 hover:text-bjp-saffron hover:bg-orange-50 rounded-lg transition-colors"
                       title="Edit & Reschedule"
                     >
                       <Edit3 className="w-4 h-4" />
                     </button>
                     <button 
                       onClick={() => handleCancelPost(post.id)}
                       className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                       title="Cancel Schedule"
                     >
                       <Trash2 className="w-4 h-4" />
                     </button>
                   </div>

                 </div>
               ))}
             </div>
           )}
         </div>
      </div>

    </div>
  );
};

export default ContentStudio;